import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';
import { CtaButton } from '@tassomutuo-backoffice/ui';
import { ConsultationRequest } from '@tassomutuo-backoffice/types';
import useDateFormat from '../../../../hooks/useDateFormat';
import ConsultationServiceTypeChip from './ConsultationServiceTypeChip';

interface IProps {
  request: ConsultationRequest;
  onOpenDetails: (id: string) => void;
}

const fullPhone = (prefix?: string, phone?: string) =>
  prefix && phone ? `${prefix} ${phone}` : prefix || phone || undefined;

const isFilled = (value?: string) => !!value && value.trim() !== '';

const ConsultationRequestCard = ({ request, onOpenDetails }: IProps) => {
  const { formatDate } = useDateFormat();

  const phone = fullPhone(request.phonePrefix, request.phone);

  const agenzia = request.agenzia_euroansa_da_cui_arriva_la_lead;
  const consulente = request.consulente_richiesto_pagina;
  const segnalatore = request.segnalatore;
  const hasAttribution =
    isFilled(agenzia) || isFilled(consulente) || isFilled(segnalatore);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        mx: 4,
        my: 2,
        p: 2,
        borderRadius: 1,
        boxShadow: (th) => `0 1px 2px ${th.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          pb: 1,
          borderBottom: (th) => `1px solid ${th.palette.divider}`,
        }}
      >
        <ConsultationServiceTypeChip serviceType={request.serviceType} />
        {request.createdAt && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            color="text.secondary"
          >
            <CalendarTodayIcon fontSize="small" />
            <Typography variant="body2">
              {formatDate(request.createdAt, 'dd/MM/yyyy HH:mm')}
            </Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ pt: 2, pb: 1 }}>
        <Typography variant="h6" fontWeight="bold" textTransform="uppercase">
          {request.fullName || '—'}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={3}
        flexWrap="wrap"
        rowGap={1}
        sx={{ color: 'text.secondary', mb: 1 }}
      >
        {request.email && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EmailIcon fontSize="small" />
            <Typography variant="body2">{request.email}</Typography>
          </Stack>
        )}
        {phone && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PhoneIcon fontSize="small" />
            <Typography variant="body2">{phone}</Typography>
          </Stack>
        )}
      </Stack>

      {hasAttribution && (
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          rowGap={1}
          sx={{ color: 'text.secondary', mb: 1 }}
        >
          {isFilled(agenzia) && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <BusinessIcon fontSize="small" />
              <Typography variant="body2">agenzia: {agenzia}</Typography>
            </Stack>
          )}
          {isFilled(consulente) && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AssignmentIndIcon fontSize="small" />
              <Typography variant="body2">consulente: {consulente}</Typography>
            </Stack>
          )}
          {isFilled(segnalatore) && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CampaignIcon fontSize="small" />
              <Typography variant="body2">
                segnalatore: {segnalatore}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <CtaButton
          variant="contained"
          onClick={() => onOpenDetails(request.id)}
        >
          Apri dettagli
        </CtaButton>
      </Box>
    </Box>
  );
};

export default ConsultationRequestCard;
